import React, {FC, useEffect, useRef, useState} from 'react'

import cn from 'classnames/bind'

import IconCloud from 'assets/icons/cloud-upload.svg'
import IconDelete from 'assets/icons/delete.svg'

import s from './DragFile.module.scss'
import ErrorMessage from '../ErrorMessage'
import FieldLabel from '../FieldLabel'
import {IFieldBase} from '../Input/Input'

const cx = cn.bind(s)

export interface IDragFileProps extends IFieldBase {
	className?: string
	name: string
	onChange: (fileSrc: string | undefined) => void
	// Max file size in MB
	maxFileSize?: number
	value: string | undefined
}

const DragFile: FC<IDragFileProps> = ({
	className,
	label,
	error,
	isError,
	required,
	onChange: onSrcChange,
	maxFileSize = 2,
	value,
	name,
}) => {
	const [errorTypeMessage, setErrorTypeMessage] = useState('')
	const dragAreaRef = useRef<HTMLDivElement>(null)
	const fileRef = useRef<HTMLInputElement>(null)
	const [fileSizeErrorMessage, setFileSizeErrorMessage] = useState('')

	const getDataUrlFromFile = (file: File) => {
		return new Promise<string>((resolve, reject) => {
			const reader = new FileReader()
			reader.readAsDataURL(file)
			reader.onloadend = (evt) => {
				resolve(evt.target?.result as string)
			}

			reader.onerror = () => {
				reject(undefined)
			}
		})
	}

	const onChange: React.ChangeEventHandler<HTMLInputElement> = async (evt) => {
		const files = evt.target?.files
		if (files && files.length > 0) {
			const file = files[0]
			setErrorTypeMessage('')
			onSrcChange('')
			setFileSizeErrorMessage('')

			if (file.type?.indexOf('image') !== 0) {
				setErrorTypeMessage('File must be an image.')
				return
			}
			if (files[0].size === 0) {
				setFileSizeErrorMessage('Your file is empty. Please choose correct file.')
				return
			}
			if (maxFileSize && maxFileSize * 1024 * 1024 < file.size) {
				setFileSizeErrorMessage(
					`Your file is bigger than ${maxFileSize}MB. Please make it smaller.`,
				)
				return
			}
			const src = await getDataUrlFromFile(file)
			onSrcChange(src)
		}
	}

	const deleteFile = () => {
		if (fileRef !== null && fileRef.current) {
			const file = fileRef.current
			file.value = ''
			onSrcChange('')
		}
	}

	useEffect(() => {
		if (dragAreaRef.current) {
			let dragCount = 0
			const dragAreaElement = dragAreaRef.current

			const handleDragenter = () => {
				dragCount++
				dragAreaElement.classList.add('highlight')
			}

			const handleDragleave = () => {
				dragCount--

				if (dragCount === 0) {
					dragAreaElement.classList.remove('highlight')
				}
			}

			const handleDrop = (evt: DragEvent) => {
				dragAreaElement.classList.remove('highlight')
				dragCount = 0

				const files = evt.dataTransfer?.files

				if (fileRef.current && files) {
					fileRef.current.files = files
					const fileEvent = new Event('change', {bubbles: true})
					fileRef.current.dispatchEvent(fileEvent)
				}
			}

			const preventDefaults = (e: Event) => {
				e.preventDefault()
				e.stopPropagation()
			}

			const events: Array<keyof HTMLElementEventMap> = [
				'dragenter',
				'dragover',
				'dragleave',
				'drop',
			]

			events.forEach((eventName) => {
				dragAreaElement.addEventListener(eventName, preventDefaults, false)
				document.addEventListener(eventName, preventDefaults)
			})

			dragAreaElement.addEventListener('dragenter', handleDragenter)
			dragAreaElement.addEventListener('dragleave', handleDragleave, {capture: true})
			dragAreaElement.addEventListener('drop', handleDrop)

			return () => {
				dragAreaElement.removeEventListener('dragenter', handleDragenter)
				dragAreaElement.removeEventListener('dragleave', handleDragleave)
				dragAreaElement.removeEventListener('drop', handleDrop)
				events.forEach((eventName) => {
					dragAreaElement.removeEventListener(eventName, preventDefaults, false)
					document.removeEventListener(eventName, preventDefaults)
				})
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const content = (
		<div className={cx('drag-area')} ref={dragAreaRef}>
			{value ? (
				// eslint-disable-next-line @next/next/no-img-element
				<img src={value} alt="invoice_logo" className={cx('image')} height="56" />
			) : (
				<>
					<IconCloud className={cx('icon')} />
					<p className={cx('text', 'fs-s')}>
						Drag file here or{' '}
						<label htmlFor={`file-input-${name}`} className={cx('link')}>
							Browse
						</label>
					</p>
				</>
			)}
			<input
				hidden
				type="file"
				id={`file-input-${name}`}
				accept="image/*"
				onChange={onChange}
				ref={fileRef}
			/>
		</div>
	)
	return (
		<div
			className={cx('wrapper', className, {
				isError: isError || !!error || !!errorTypeMessage || !!fileSizeErrorMessage,
			})}>
			{value && <IconDelete className={cx('delete')} onClick={deleteFile} />}
			{label ? (
				<FieldLabel as="div" {...{label, required}}>
					{content}
				</FieldLabel>
			) : (
				content
			)}
			<ErrorMessage error={error || fileSizeErrorMessage || errorTypeMessage} />
		</div>
	)
}

DragFile.displayName = 'DragFile'

export default DragFile
